import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API, { SERVER_URL } from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { S } from '../../components/pageStyles';
import { Loader, StatCard } from '../../components/ui';
import { LuBan, LuBanknote, LuCircleCheck, LuCreditCard, LuEye, LuFileText, LuGift, LuPackage, LuReceipt, LuShoppingCart, LuSmartphone, LuStore, LuWallet, LuX } from 'react-icons/lu';

import { fmt, fmtF } from '../../theme';
export default function Ventes() {
  const { utilisateur } = useAuth();
  const [ventes, setVentes] = useState([]);
  const [etablissements, setEtablissements] = useState([]);
  const [produits, setProduits] = useState([]);
  const [kits, setKits] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [vue, setVue] = useState('liste');
  const [venteDetail, setVenteDetail] = useState(null);
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(1);
  const parPage = 10;

  const [filtreEtab, setFiltreEtab] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  const [form, setForm] = useState({
    etablissement_id: '',
    mode_paiement: 'especes',
    note: '',
    produits: [],
    kits: []
  });

  const [modalAnnulation, setModalAnnulation] = useState(null);
  const [motifAnnulation, setMotifAnnulation] = useState('');

  useEffect(() => { chargerDonnees(); }, []);

  useEffect(() => {
    if (form.etablissement_id) {
      chargerProduitsKits(form.etablissement_id);
    } else {
      setProduits([]);
      setKits([]);
    }
  }, [form.etablissement_id]);

  const chargerDonnees = async () => {
    try {
      const [ventesRes, etabRes] = await Promise.all([
        API.get('/ventes'),
        API.get('/etablissements')
      ]);
      setVentes(ventesRes.data.ventes || []);
      setEtablissements(etabRes.data.etablissements || []);
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

 const chargerProduitsKits = async (etabId) => {
  try {
    const [produitsRes, kitsRes] = await Promise.all([
      API.get('/produits'),
      API.get('/kits')
    ]);

    // Produits disponibles (stock > 0)
    const produitsDisponibles = (produitsRes.data.produits || []).filter(
      p => p.etablissement_id === parseInt(etabId) && p.quantite > 0
    );
    setProduits(produitsDisponibles);

    // Kits disponibles — vérifier que tous les produits ont assez de stock
    const tousLesProduits = produitsRes.data.produits || [];
    const kitsDisponibles = (kitsRes.data.kits || []).filter(k => {
      if (k.etablissement_id !== parseInt(etabId) || !k.actif) return false;
      // Vérifier chaque produit du kit
      if (!k.produits || k.produits.length === 0) return false;
      return k.produits.every(kp => {
        const produit = tousLesProduits.find(p => p.id === kp.produit_id);
        return produit && produit.quantite >= kp.quantite;
      });
    });
    setKits(kitsDisponibles);

  } catch (err) {
    console.error(err);
  }
};

  const voirDetail = async (vente) => {
    try {
      const res = await API.get('/ventes/' + vente.id);
      setVenteDetail(res.data);
      setVue('detail');
    } catch (err) {
      setMessage('❌ Erreur chargement détail');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.produits.length === 0 && form.kits.length === 0) {
      setMessage('❌ Ajoutez au moins un produit ou kit');
      return;
    }
    try {
      await API.post('/ventes', form);
      setMessage('✅ Vente enregistrée avec succès !');
      chargerDonnees();
      setVue('liste');
      resetForm();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const handleAnnuler = async () => {
    if (!motifAnnulation) {
      setMessage('❌ Le motif est obligatoire');
      return;
    }
    try {
      await API.patch('/ventes/' + modalAnnulation + '/annuler', {
        motif_annulation: motifAnnulation
      });
      setMessage('✅ Vente annulée !');
      setModalAnnulation(null);
      setMotifAnnulation('');
      chargerDonnees();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const ajouterProduit = (produitId) => {
    const id = parseInt(produitId);
    if (form.produits.find(p => p.produit_id === id)) return;
    setForm({ ...form, produits: [...form.produits, { produit_id: id, quantite: 1 }] });
  };

  const modifierQteProduit = (produitId, quantite) => {
    setForm({
      ...form,
      produits: form.produits.map(p =>
        p.produit_id === produitId ? { ...p, quantite: Math.max(1, parseInt(quantite) || 1) } : p
      )
    });
  };

  const retirerProduit = (produitId) => {
    setForm({ ...form, produits: form.produits.filter(p => p.produit_id !== produitId) });
  };

  const ajouterKit = (kitId) => {
    const id = parseInt(kitId);
    if (form.kits.find(k => k.kit_id === id)) return;
    setForm({ ...form, kits: [...form.kits, { kit_id: id, quantite: 1 }] });
  };

  const modifierQteKit = (kitId, quantite) => {
    setForm({
      ...form,
      kits: form.kits.map(k =>
        k.kit_id === kitId ? { ...k, quantite: Math.max(1, parseInt(quantite) || 1) } : k
      )
    });
  };

  const retirerKit = (kitId) => {
    setForm({ ...form, kits: form.kits.filter(k => k.kit_id !== kitId) });
  };

  const totalProduits = form.produits.reduce((t, item) => {
    const p = produits.find(p => p.id === item.produit_id);
    return t + (p ? parseFloat(p.prix) * item.quantite : 0);
  }, 0);

  const totalKits = form.kits.reduce((t, item) => {
    const k = kits.find(k => k.id === item.kit_id);
    return t + (k ? parseFloat(k.prix_kit) * item.quantite : 0);
  }, 0);

  const totalGeneral = totalProduits + totalKits;

  const resetForm = () => {
    setForm({ etablissement_id: '', mode_paiement: 'especes', note: '', produits: [], kits: [] });
    setProduits([]);
    setKits([]);
  };

  const ventesFiltrees = ventes.filter(v => {
    const matchEtab = !filtreEtab || v.etablissement_id === parseInt(filtreEtab);
    const matchStatut = !filtreStatut || v.statut === filtreStatut;
    return matchEtab && matchStatut;
  });

  const totalPages = Math.ceil(ventesFiltrees.length / parPage);
  const ventesPage = ventesFiltrees.slice((page - 1) * parPage, page * parPage);

  const aujourd_hui = new Date().toDateString();
  const ventesJour = ventes.filter(v =>
    new Date(v.created_at).toDateString() === aujourd_hui && v.statut === 'validee'
  );
  const caJour = ventesJour.reduce((t, v) => t + parseFloat(v.montant_total || 0), 0);

  if (chargement) return <Loader />;

  return (
    <AppLayout role="admin">

      {/* CONTENU */}
      <div style={styles.contenu}>

        {message && (
          <div role="status" style={{
            ...styles.messageBox,
            background: message.startsWith('✅') ? '#ECFDF5' : '#FEF2F2',
            color: message.startsWith('✅') ? '#047857' : '#B91C1C'
          }}>{message.replace(/^(✅|❌|⚠️)\s*/, '')}</div>
        )}

        {/* MODAL ANNULATION */}
        {modalAnnulation && (
          <div style={styles.modalOverlay}>
            <div style={styles.modal}>
              <h3 style={{ margin: '0 0 16px', color: '#DC2626' }}><LuBan aria-hidden style={{ verticalAlign: '-0.125em' }} /> Annuler la vente</h3>
              <p style={{ color: '#475569', fontSize: 13, marginBottom: 12 }}>
                Le stock sera automatiquement remis à jour.
              </p>
              <label style={styles.label}>Motif d'annulation *</label>
              <textarea
                value={motifAnnulation}
                onChange={(e) => setMotifAnnulation(e.target.value)}
                style={{ ...styles.input, height: 80, resize: 'vertical', marginTop: 6, marginBottom: 16 }}
                placeholder="Ex: Erreur de commande, client insatisfait..."
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => { setModalAnnulation(null); setMotifAnnulation(''); }}
                  style={styles.btnAnnuler}>Annuler</button>
                <button onClick={handleAnnuler} style={styles.btnConfirmerAnnulation}>
                  <LuCircleCheck aria-hidden style={{ verticalAlign: '-0.125em' }} /> Confirmer l'annulation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitre}>Ventes</h1>
            <p style={styles.headerSousTitre}>
              {"Aujourd'hui : " + ventesJour.length + ' vente(s) • ' +
               fmtF(caJour)}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {vue === 'liste' ? (
              <button onClick={() => { resetForm(); setVue('nouvelle'); }}
                style={styles.boutonAjouter}>
                + Nouvelle Vente
              </button>
            ) : (
              <button onClick={() => setVue('liste')} style={styles.boutonRetour}>
                ← Retour à la liste
              </button>
            )}
          </div>
        </div>

        {/* STATS RAPIDES */}
        {vue === 'liste' && (
          <div style={styles.statsGrid}>
            {[
              {
                icon: <LuShoppingCart aria-hidden />, label: "Ventes aujourd'hui",
                valeur: ventesJour.length, couleur: '#2563EB', bg: '#EFF6FF'
              },
              {
                icon: <LuWallet aria-hidden />, label: "Revenus du jour",
                valeur: fmtF(caJour),
                couleur: '#059669', bg: '#ECFDF5'
              },
              {
                icon: <LuBanknote aria-hidden />, label: 'Espèces',
                valeur: fmtF(ventesJour.filter(v => v.mode_paiement === 'especes')
                  .reduce((t, v) => t + parseFloat(v.montant_total || 0), 0)),
                couleur: '#EA580C', bg: '#FFF7ED'
              },
              {
                icon: <LuSmartphone aria-hidden />, label: 'Mobile Money',
                valeur: fmtF(ventesJour.filter(v => v.mode_paiement === 'mobile_money')
                  .reduce((t, v) => t + parseFloat(v.montant_total || 0), 0)),
                couleur: '#7C3AED', bg: '#F5F3FF'
              },
            ].map((stat, i) => (
              <StatCard key={i} icon={stat.icon} label={stat.label} value={stat.valeur} color={stat.couleur} />
            ))}
          </div>
        )}

        {/* ── LISTE ── */}
        {vue === 'liste' && (
          <>
            <div style={styles.filtres}>
              <select
                value={filtreEtab}
                onChange={(e) => { setFiltreEtab(e.target.value); setPage(1); }}
                style={styles.filtreSelect}
              >
                <option value="">Tous les établissements</option>
                {etablissements.map(e => (
                  <option key={e.id} value={e.id}>{e.nom}</option>
                ))}
              </select>
              <select
                value={filtreStatut}
                onChange={(e) => { setFiltreStatut(e.target.value); setPage(1); }}
                style={styles.filtreSelect}
              >
                <option value="">Tous les statuts</option>
                <option value="validee">Validées</option>
                <option value="annulee">Annulées</option>
              </select>
              <input type="date" value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)} style={styles.filtreDate} />
              <input type="date" value={dateFin}
                onChange={(e) => setDateFin(e.target.value)} style={styles.filtreDate} />
            </div>

            <div style={styles.section}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    {['#', 'Facture', 'Date & Heure', 'Établissement', 'Serveur',
                      'Articles', 'Paiement', 'Montant', 'Statut', 'Actions'].map((h, i) => (
                      <th key={i} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ventesPage.map((vente, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                      <td style={styles.td}>
                        <span style={styles.ventId}>{'#' + vente.id}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#2563EB' }}>
                          {vente.numero_facture || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: 13 }}>
                          {new Date(vente.created_at).toLocaleDateString('fr-FR')}
                        </div>
                        <div style={{ color: '#64748B', fontSize: 11 }}>
                          {new Date(vente.created_at).toLocaleTimeString('fr-FR', {
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                      </td>
                      <td style={styles.td}>{vente.etablissement_nom}</td>
                      <td style={styles.td}>
                        {vente.serveur_prenom + ' ' + vente.serveur_nom}
                      </td>
                      <td style={styles.td}>
                        <span style={styles.articlesCount}>
                          {vente.nombre_articles + ' article(s)'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 8, fontSize: 11,
                          background: vente.mode_paiement === 'especes' ? '#FFF7ED' : '#F5F3FF',
                          color: vente.mode_paiement === 'especes' ? '#EA580C' : '#7C3AED',
                          fontWeight: 700
                        }}>
                          {vente.mode_paiement === 'especes' ? 'Espèces' : 'Mobile Money'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontWeight: 700, color: '#059669' }}>
                          {fmtF(parseInt(vente.montant_total))}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '3px 10px', borderRadius: 12, fontSize: 11,
                          fontWeight: 700,
                          background: vente.statut === 'validee' ? '#ECFDF5' : '#FEF2F2',
                          color: vente.statut === 'validee' ? '#059669' : '#DC2626'
                        }}>
                          {vente.statut === 'validee' ? 'Validée' : 'Annulée'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => voirDetail(vente)} style={styles.btnVoir} aria-label="Voir le détail" title="Voir le détail">
                            <LuEye aria-hidden style={{ verticalAlign: '-0.125em' }} />
                          </button>
                          {vente.statut === 'validee' &&
                           ['admin', 'gerant'].includes(utilisateur?.role) && (
                            <button onClick={() => setModalAnnulation(vente.id)}
                              style={styles.btnAnnulerVente} aria-label="Annuler la vente" title="Annuler la vente">
                              <LuBan aria-hidden style={{ verticalAlign: '-0.125em' }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {ventesFiltrees.length === 0 && (
                <div style={styles.vide}>
                  <div style={{ fontSize: 48 }}><LuShoppingCart aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                  <p>Aucune vente trouvée</p>
                </div>
              )}

              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{ ...styles.pagBtn, opacity: page === 1 ? 0.4 : 1 }}>←</button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button key={i} onClick={() => setPage(i + 1)} style={{
                      ...styles.pagNum,
                      background: page === i + 1 ? 'var(--accent)' : 'white',
                      color: page === i + 1 ? 'white' : '#475569'
                    }}>{i + 1}</button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{ ...styles.pagBtn, opacity: page === totalPages ? 0.4 : 1 }}>→</button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── DÉTAIL VENTE ── */}
        {vue === 'detail' && venteDetail && (
          <div style={styles.detailCard}>
            <div style={styles.detailHeader}>
              <div>
                <span style={styles.ventId}>{'#' + venteDetail.vente.id}</span>
                {venteDetail.vente.numero_facture && (
                  <span style={{ ...styles.ventId, marginLeft: 8, background: '#ECFDF5', color: '#059669' }}>
                    {' ' + venteDetail.vente.numero_facture}
                  </span>
                )}
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0F172A', margin: '8px 0 4px' }}>
                  {'Vente — ' + venteDetail.vente.etablissement_nom}
                </h2>
                <div style={{ color: '#64748B', fontSize: 13 }}>
                  {' ' + venteDetail.vente.serveur_prenom + ' ' + venteDetail.vente.serveur_nom +
                   ' • ' + new Date(venteDetail.vente.created_at).toLocaleString('fr-FR')}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#059669' }}>
                  {fmtF(parseInt(venteDetail.vente.montant_total))}
                </div>
                <span style={{
                  padding: '4px 12px', borderRadius: 12, fontSize: 13, fontWeight: 700,
                  background: venteDetail.vente.statut === 'validee' ? '#ECFDF5' : '#FEF2F2',
                  color: venteDetail.vente.statut === 'validee' ? '#059669' : '#DC2626'
                }}>
                  {venteDetail.vente.statut === 'validee' ? 'Validée' : 'Annulée'}
                </span>
              </div>
            </div>

            {venteDetail.vente.motif_annulation && (
              <div style={styles.motifAnnulation}>
                {'Motif annulation : ' + venteDetail.vente.motif_annulation}
              </div>
            )}

            <div style={styles.sectionDetail}>
              <h3 style={styles.sectionDetailTitre}>
                {'Articles (' + venteDetail.details.length + ')'}
              </h3>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    {['Article', 'Type', 'Quantité', 'Prix unitaire', 'Sous-total'].map((h, i) => (
                      <th key={i} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {venteDetail.details.map((detail, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                      <td style={styles.td}>
                        <span style={{ fontWeight: 700 }}>
                          {detail.produit_nom || detail.kit_nom}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 8, fontSize: 11,
                          background: detail.produit_id ? '#EFF6FF' : '#ECFDF5',
                          color: detail.produit_id ? '#2563EB' : '#059669', fontWeight: 700
                        }}>
                          {detail.produit_id ? 'Produit' : 'Kit'}
                        </span>
                      </td>
                      <td style={styles.td}>{detail.quantite}</td>
                      <td style={styles.td}>
                        {fmtF(parseInt(detail.prix_unitaire))}
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontWeight: 700, color: '#059669' }}>
                          {fmtF(parseInt(detail.sous_total))}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={styles.detailFooter}>
              <div style={styles.detailPaiement}>
                <span style={{ color: '#475569' }}>Mode de paiement :</span>
                <span style={{ fontWeight: 700, marginLeft: 8 }}>
                  {venteDetail.vente.mode_paiement === 'especes' ? 'Espèces' : 'Mobile Money'}
                </span>
              </div>
              <div style={styles.detailTotal}>
                <span style={{ color: '#475569' }}>Total :</span>
                <span style={{ fontWeight: 700, fontSize: 20, color: '#059669', marginLeft: 12 }}>
                  {fmtF(parseInt(venteDetail.vente.montant_total))}
                </span>
              </div>
            </div>

            {venteDetail.vente.statut === 'validee' &&
             ['admin', 'gerant'].includes(utilisateur?.role) && (
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button onClick={() => setModalAnnulation(venteDetail.vente.id)}
                  style={styles.btnConfirmerAnnulation}>
                  <LuBan aria-hidden style={{ verticalAlign: '-0.125em' }} /> Annuler cette vente
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── NOUVELLE VENTE ── */}
        {vue === 'nouvelle' && (
          <div style={styles.venteLayout}>

            {/* PANNEAU GAUCHE — CATALOGUE */}
            <div style={styles.catalogue}>
              <div style={styles.catalogueTitre}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /> Établissement & Catalogue</div>

              <select
                value={form.etablissement_id}
                onChange={(e) => setForm({ ...form, etablissement_id: e.target.value, produits: [], kits: [] })}
                style={{ ...styles.select, marginBottom: 16 }}
              >
                <option value="">Sélectionnez un établissement</option>
                {etablissements.map(e => (
                  <option key={e.id} value={e.id}>{e.nom}</option>
                ))}
              </select>

              {form.etablissement_id && (
                <>
                  {produits.length > 0 && (
                    <>
                      <div style={styles.catalogueSousTitre}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /> Produits</div>
                      <div style={styles.catalogueItems}>
                        {produits.map((p, i) => (
                          <div key={i} onClick={() => ajouterProduit(p.id)}
                            style={{
                              ...styles.catalogueItem,
                              opacity: form.produits.find(fp => fp.produit_id === p.id) ? 0.5 : 1
                            }}
                          >
                            {p.image_principale ? (
                              <img
                                src={SERVER_URL + '/uploads/produits/' +
                                  p.image_principale.replace(/\\/g, '/').split('/').pop()}
                                alt={p.nom}
                                style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }}
                              />
                            ) : (
                              <div style={styles.itemAvatar}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                            )}
                           <div style={{
                            fontSize: 11,
                            color: p.quantite <= p.seuil_alerte ? '#EA580C' : '#64748B',
                            fontWeight: p.quantite <= p.seuil_alerte ? 'bold' : 'normal'
                          }}>
                            {p.unite === 'bouteille_gaz'
                              ? ('Stock: ' + p.quantite + ' bouteille(s)')
                              : p.quantite <= p.seuil_alerte
                                ? ('Stock faible : ' + p.quantite + ' ' + p.unite)
                                : ('Stock: ' + p.quantite + ' ' + p.unite)}
                          </div>
                            <div style={{ fontWeight: 700, color: '#2563EB', fontSize: 13 }}>
                              {fmt(parseInt(p.prix))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {kits.length > 0 && (
                    <>
                      <div style={{ ...styles.catalogueSousTitre, marginTop: 12 }}><LuGift aria-hidden style={{ verticalAlign: '-0.125em' }} /> Kits</div>
                      <div style={styles.catalogueItems}>
                        {kits.map((k, i) => (
                          <div key={i} onClick={() => ajouterKit(k.id)}
                            style={{
                              ...styles.catalogueItem, background: '#F0FFF4',
                              opacity: form.kits.find(fk => fk.kit_id === k.id) ? 0.5 : 1
                            }}
                          >
                            <div style={{ ...styles.itemAvatar, background: '#ECFDF5', color: '#059669' }}>
                              <LuGift aria-hidden style={{ verticalAlign: '-0.125em' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 13 }}>{k.nom}</div>
                              <div style={{ color: '#64748B', fontSize: 11 }}>
                                {(k.produits || []).length + ' produits'}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: '#059669', fontSize: 13 }}>
                              {fmt(parseInt(k.prix_kit))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {produits.length === 0 && kits.length === 0 && (
                    <div style={{ color: '#64748B', textAlign: 'center', padding: 20, fontSize: 13 }}>
                      Aucun produit disponible pour cet établissement
                    </div>
                  )}
                </>
              )}
            </div>

            {/* PANNEAU DROIT — PANIER */}
            <div style={styles.panier}>
              <div style={styles.panierTitre}><LuShoppingCart aria-hidden style={{ verticalAlign: '-0.125em' }} /> Panier</div>

              {form.produits.length === 0 && form.kits.length === 0 ? (
                <div style={styles.panierVide}>
                  <div style={{ fontSize: 40 }}><LuShoppingCart aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                  <p>Panier vide</p>
                  <p style={{ fontSize: 12 }}>Cliquez sur un produit ou kit pour l'ajouter</p>
                </div>
              ) : (
                <div style={styles.panierItems}>
                  {form.produits.map((item, i) => {
                    const p = produits.find(p => p.id === item.produit_id);
                    if (!p) return null;
                    return (
                      <div key={i} style={styles.panierItem}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{p.nom}</div>
                          <div style={{ color: '#64748B', fontSize: 11 }}>
                            {fmt(parseInt(p.prix)) + ' FCFA/unité'}
                          </div>
                        </div>
                        <div style={styles.panierItemControls}>
                          <button onClick={() => modifierQteProduit(item.produit_id, item.quantite - 1)}
                            style={styles.btnQte}>−</button>
                          <span style={styles.qteVal}>{item.quantite}</span>
                          <button onClick={() => modifierQteProduit(item.produit_id, item.quantite + 1)}
                            style={styles.btnQte}>+</button>
                          <span style={styles.itemSousTotal}>
                            {fmt((parseInt(p.prix) * item.quantite))}
                          </span>
                          <button onClick={() => retirerProduit(item.produit_id)}
                            style={styles.btnRetirer} aria-label="Retirer" title="Retirer"><LuX aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                        </div>
                      </div>
                    );
                  })}

                  {form.kits.map((item, i) => {
                    const k = kits.find(k => k.id === item.kit_id);
                    if (!k) return null;
                    return (
                      <div key={i} style={{ ...styles.panierItem, background: '#F0FFF4' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{' ' + k.nom}</div>
                          <div style={{ color: '#64748B', fontSize: 11 }}>
                            {fmt(parseInt(k.prix_kit)) + ' FCFA/kit'}
                          </div>
                        </div>
                        <div style={styles.panierItemControls}>
                          <button onClick={() => modifierQteKit(item.kit_id, item.quantite - 1)}
                            style={styles.btnQte}>−</button>
                          <span style={styles.qteVal}>{item.quantite}</span>
                          <button onClick={() => modifierQteKit(item.kit_id, item.quantite + 1)}
                            style={styles.btnQte}>+</button>
                          <span style={styles.itemSousTotal}>
                            {fmt((parseInt(k.prix_kit) * item.quantite))}
                          </span>
                          <button onClick={() => retirerKit(item.kit_id)}
                            style={styles.btnRetirer} aria-label="Retirer" title="Retirer"><LuX aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* INFOS PAIEMENT */}
              <div style={styles.panierInfos}>
                <div style={styles.champGroupe}>
                  <label style={styles.label}><LuCreditCard aria-hidden style={{ verticalAlign: '-0.125em' }} /> Mode de paiement</label>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    {[
                      { val: 'especes', label: 'Espèces' },
                      { val: 'mobile_money', label: 'Mobile Money' }
                    ].map(mode => (
                      <button key={mode.val} type="button"
                        onClick={() => setForm({ ...form, mode_paiement: mode.val })}
                        style={{
                          flex: 1, padding: '10px',
                          border: '2px solid ' + (form.mode_paiement === mode.val ? '#059669' : '#E2E8F0'),
                          borderRadius: 10, cursor: 'pointer', fontWeight: 700,
                          background: form.mode_paiement === mode.val ? '#ECFDF5' : 'white',
                          color: form.mode_paiement === mode.val ? '#059669' : '#475569', fontSize: 13
                        }}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuFileText aria-hidden style={{ verticalAlign: '-0.125em' }} /> Note</label>
                    <input
                      type="text"
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      style={styles.input}
                      placeholder="Note optionnelle..."
                    />
                  </div>
                </div>

                <div style={{ marginTop: 8 }}>
                  <label style={styles.label}><LuReceipt aria-hidden style={{ verticalAlign: '-0.125em' }} /> N° Facture</label>
                  <input
                    type="text"
                    style={{ ...styles.input, background: '#F8FAFC', color: '#64748B', marginTop: 6 }}
                    placeholder="Généré automatiquement"
                    disabled
                  />
                </div>
              </div>

              {/* TOTAL & VALIDER */}
              <div style={styles.panierTotal}>
                <div style={styles.totalLigne}>
                  <span style={{ color: '#475569' }}>Sous-total produits</span>
                  <span>{fmtF(totalProduits)}</span>
                </div>
                {totalKits > 0 && (
                  <div style={styles.totalLigne}>
                    <span style={{ color: '#475569' }}>Sous-total kits</span>
                    <span>{fmtF(totalKits)}</span>
                  </div>
                )}
                <div style={{ ...styles.totalLigne, ...styles.totalFinal }}>
                  <span>TOTAL</span>
                  <span>{fmtF(totalGeneral)}</span>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={!form.etablissement_id ||
                    (form.produits.length === 0 && form.kits.length === 0)}
                  style={{
                    ...styles.btnValiderVente,
                    opacity: (!form.etablissement_id ||
                      (form.produits.length === 0 && form.kits.length === 0)) ? 0.5 : 1
                  }}
                >
                  {'Valider la vente — ' + fmtF(totalGeneral)}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

const styles = {
  chargement: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', height: '100vh', fontSize: 24, color: '#475569'
  },
  contenu: { ...S.contenu, flex: 1 },
  messageBox: { ...S.messageBox, marginBottom: 16 },
  modalOverlay: S.modalOverlay,
  modal: { ...S.modal, width: 'min(480px, 100%)' },
  header: S.header,
  headerTitre: S.headerTitre,
  headerSousTitre: S.headerSousTitre,
  boutonAjouter: S.boutonAjouter,
  boutonRetour: S.boutonRetour,
  statsGrid: { ...S.statsGrid, marginBottom: 20 },
  statCard: S.statCard,
  filtres: { ...S.filtres, marginBottom: 16 },
  filtreSelect: { ...S.filtreSelect, minWidth: 180 },
  filtreDate: {
    padding: '10px 14px', borderRadius: 10,
    border: '2px solid #E2E8F0', fontSize: 13, outline: 'none'
  },
  section: S.section,
  table: { ...S.table, width: '100%' },
  tableHeader: S.tableHeader,
  th: S.th,
  td: S.td,
  ventId: {
    background: '#EFF6FF', color: '#2563EB',
    padding: '3px 8px', borderRadius: 6,
    fontSize: 12, fontWeight: 700, fontFamily: 'monospace'
  },
  articlesCount: {
    background: '#F8FAFC', color: '#475569',
    padding: '3px 8px', borderRadius: 8, fontSize: 11
  },
  btnVoir: S.btnVoir,
  btnAnnulerVente: S.btnAnnulerVente,
  vide: S.vide,
  pagination: { ...S.pagination, marginTop: 16 },
  pagBtn: S.pagBtn,
  pagNum: { ...S.pagNum, width: 30 },
  detailCard: {
    background: 'white', borderRadius: 16,
    padding: 32, boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  detailHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: 24,
    paddingBottom: 20, borderBottom: '1px solid #F1F5F9'
  },
  motifAnnulation: {
    background: '#FEF2F2', color: '#DC2626',
    padding: '10px 16px', borderRadius: 8, fontSize: 13, marginBottom: 20
  },
  sectionDetail: {
    marginBottom: 24, padding: 20,
    background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0'
  },
  sectionDetailTitre: { fontSize: 15, fontWeight: 700, color: '#0F172A', marginBottom: 16 },
  detailFooter: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '16px 20px',
    background: '#F8FAFC', borderRadius: 12
  },
  detailPaiement: { fontSize: 14, color: '#1E293B' },
  detailTotal: { fontSize: 14, color: '#1E293B' },
  venteLayout: {
    display: 'grid', gridTemplateColumns: '1fr 380px',
    gap: 20, alignItems: 'start'
  },
  catalogue: {
    background: 'white', borderRadius: 14, padding: 20,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    maxHeight: 'calc(100vh - 180px)', overflowY: 'auto'
  },
  catalogueTitre: { fontWeight: 700, fontSize: 16, color: '#0F172A', marginBottom: 16 },
  catalogueSousTitre: {
    fontWeight: 700, fontSize: 13, color: '#475569',
    marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1
  },
  catalogueItems: { display: 'flex', flexDirection: 'column', gap: 8 },
  catalogueItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px', background: '#F8FAFC',
    borderRadius: 10, cursor: 'pointer',
    border: '2px solid transparent', transition: 'all 0.2s'
  },
  itemAvatar: {
    width: 40, height: 40, borderRadius: 8,
    background: '#EFF6FF', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 20
  },
  panier: {
    background: 'white', borderRadius: 14, padding: 20,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    position: 'sticky', top: 20,
    maxHeight: 'calc(100vh - 180px)', overflowY: 'auto'
  },
  panierTitre: { fontWeight: 700, fontSize: 16, color: '#0F172A', marginBottom: 16 },
  panierVide: { textAlign: 'center', padding: '30px 20px', color: '#64748B', fontSize: 13 },
  panierItems: { marginBottom: 16 },
  panierItem: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 12px', background: '#F8FAFC',
    borderRadius: 10, marginBottom: 8
  },
  panierItemControls: { display: 'flex', alignItems: 'center', gap: 6 },
  btnQte: {
    width: 26, height: 26, borderRadius: '50%',
    background: '#EFF6FF', color: '#2563EB', border: 'none', cursor: 'pointer',
    fontWeight: 700, fontSize: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  qteVal: { fontWeight: 700, fontSize: 14, minWidth: 20, textAlign: 'center' },
  itemSousTotal: { fontWeight: 700, color: '#2563EB', fontSize: 12, minWidth: 60, textAlign: 'right' },
  btnRetirer: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#DC2626' },
  panierInfos: { borderTop: '1px solid #F1F5F9', paddingTop: 16, marginBottom: 16 },
  panierTotal: { borderTop: '2px solid #F1F5F9', paddingTop: 16 },
  totalLigne: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: 13, marginBottom: 8, color: '#1E293B'
  },
  totalFinal: {
    fontWeight: 700, fontSize: 16, color: '#0F172A',
    paddingTop: 8, borderTop: '1px solid #E2E8F0', marginBottom: 16
  },
  btnValiderVente: {
    width: '100%', padding: '14px',
    background: 'var(--accent)',
    color: 'white', border: 'none', borderRadius: 12,
    cursor: 'pointer', fontWeight: 700, fontSize: 15
  },
  champGroupe: S.champGroupe,
  label: S.label,
  input: { ...S.input, width: '100%' },
  select: { ...S.select, width: '100%' },
  btnAnnuler: S.btnAnnuler,
  btnConfirmerAnnulation: S.btnConfirmerAnnulation
};